import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import LiveClassRoutineHost from '../live/LiveClassRoutineHost';
import ClassRecordingPreviewModal from '../live/ClassRecordingPreviewModal';
import { Calendar, Users, Video, CheckCircle, PlayCircle } from 'lucide-react';
import './d28d-admin-surfaces.css';

const DISPLAY_TZ = 'America/Bogota';

function dateKeyInTz(value, timeZone = DISPLAY_TZ) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date(value));
  } catch {
    return String(value).slice(0, 10);
  }
}

function todayKey() {
  return dateKeyInTz(new Date(), DISPLAY_TZ);
}

function formatClassWhen(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('es-CO', {
      timeZone: DISPLAY_TZ,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function D28dHostDailyPanel({ onBack = null }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean);
  const isGymHost = roles.includes('entrenador_gym');
  const [classes, setClasses] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [zoomLinkDraft, setZoomLinkDraft] = useState('');
  const [previewRecording, setPreviewRecording] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const loadRecordings = useCallback(async () => {
    try {
      const r = await api.get('/live-classes/admin/class-gallery');
      setRecordings(r.data?.data || []);
    } catch {
      setRecordings([]);
    }
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await api.get('/live-classes/admin');
      const list = (r.data?.data || []).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setClasses(list);
      setSelectedId((prev) => {
        if (prev && list.some((c) => String(c.id) === String(prev))) return prev;
        return list[0] ? String(list[0].id) : '';
      });
    } catch (err) {
      setClasses([]);
      setError(err.response?.data?.error || t('d28d.host.load_error', 'No se pudieron cargar tus clases.'));
    }
  }, [t]);

  useEffect(() => {
    load();
    loadRecordings();
  }, [load, loadRecordings]);

  const todayClasses = useMemo(
    () => classes.filter((c) => dateKeyInTz(c.start_time) === todayKey()),
    [classes],
  );

  const pickerClasses = useMemo(
    () => (todayClasses.length > 0 ? todayClasses : classes),
    [todayClasses, classes],
  );

  const selected = pickerClasses.find((c) => String(c.id) === String(selectedId)) || pickerClasses[0] || null;

  const recordingByClassId = useMemo(() => {
    const map = new Map();
    recordings.forEach((rec) => {
      if (rec.live_class_id != null && rec.video_url) {
        map.set(Number(rec.live_class_id), rec);
      }
    });
    return map;
  }, [recordings]);

  const publishedRecordings = useMemo(
    () => recordings.filter((rec) => rec.video_url),
    [recordings],
  );

  useEffect(() => {
    if (selected) {
      setRecordingUrl('');
      setZoomLinkDraft(selected.zoom_link || '');
    }
  }, [selected?.id, selected?.zoom_link]);

  const markAttendance = async (userId, present) => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/live-classes/admin/${selected.id}/attendance`, { user_id: userId, present });
      await load();
      setMsg(t('d28d.host.attendance_ok', 'Asistencia actualizada'));
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.host.attendance_err', 'Error al registrar asistencia'));
    } finally {
      setBusy(false);
    }
  };

  const saveZoomLink = async () => {
    if (!selected || !zoomLinkDraft.trim()) return;
    setBusy(true);
    setError('');
    setMsg('');
    try {
      const resp = await api.patch(`/live-classes/admin/${selected.id}/zoom-link`, {
        zoom_link: zoomLinkDraft.trim(),
      });
      setMsg(resp.data?.message || t('d28d.host.zoom_ok', 'Enlace Zoom publicado para tus alumnos'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.host.zoom_err', 'No se pudo guardar el enlace Zoom'));
    } finally {
      setBusy(false);
    }
  };

  const publishRecording = async () => {
    if (!selected || !recordingUrl.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/live-classes/admin/class-gallery/from-live-class/${selected.id}`, { video_url: recordingUrl.trim() });
      setMsg(isGymHost
        ? t('d28d.host.recording_ok_gym', 'Grabación publicada en la galería de clases')
        : t('d28d.host.recording_ok', 'Grabación publicada en galería D28D'));
      setRecordingUrl('');
      await loadRecordings();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.host.recording_err', 'No se pudo publicar la grabación'));
    } finally {
      setBusy(false);
    }
  };

  const enrolled = (selected?.enrolled_users || []).length
    ? selected.enrolled_users
    : (selected?.enrolled_user_ids || []).map((uid) => ({ user_id: uid, nombre: null }));
  const selectedRecording = selected ? recordingByClassId.get(Number(selected.id)) : null;

  return (
    <div className="d28d-admin-surface surface-readable dashboard-main-view space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-stone-500">
            {isGymHost
              ? t('d28d.host.eyebrow_gym', 'Panel del entrenador')
              : t('d28d.host.eyebrow', 'Panel host D28D')}
          </p>
          <h1 className="text-2xl font-bold text-stone-900">{t('d28d.host.title', 'Mi día — clases en vivo')}</h1>
          <p className="text-sm text-stone-600">{user?.nombre}</p>
        </div>
        {onBack ? <button type="button" className="btn-secondary" onClick={onBack}>{t('common.back', 'Volver')}</button> : null}
      </header>

      {error && <p className="text-sm text-red-700 bg-red-50 p-3 rounded-lg">{error}</p>}
      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg">{msg}</p>}

      {publishedRecordings.length > 0 ? (
        <section className="card p-4 space-y-3">
          <h2 className="font-semibold flex items-center gap-2">
            <PlayCircle className="w-4 h-4" />
            {t('d28d.host.recordings_list', 'Clases grabadas')}
          </h2>
          <p className="text-xs text-stone-500">
            {t('d28d.host.recordings_hint', 'Haz clic para ver la grabación en un modal.')}
          </p>
          <div className="flex flex-wrap gap-2">
            {publishedRecordings.map((rec) => (
              <button
                key={rec.id}
                type="button"
                className="btn-secondary text-sm inline-flex items-center gap-1.5"
                onClick={() => setPreviewRecording(rec)}
              >
                <Video className="w-3.5 h-3.5" />
                {rec.title || `Clase #${rec.live_class_id}`}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="card p-4 space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {todayClasses.length > 0
            ? t('d28d.host.today', 'Clases de hoy')
            : t('d28d.host.your_classes', 'Tus clases asignadas')}
        </h2>
        {pickerClasses.length === 0 ? (
          <p className="text-sm text-stone-600">{t('d28d.host.no_classes', 'No tienes clases asignadas.')}</p>
        ) : (
          <>
            {todayClasses.length === 0 ? (
              <p className="text-sm text-stone-500">
                {t('d28d.host.no_today_pick', 'No hay clases hoy. Selecciona una de tus clases programadas:')}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {pickerClasses.map((c) => {
                const hasRecording = recordingByClassId.has(Number(c.id));
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`btn-secondary text-sm ${String(selectedId) === String(c.id) ? 'ring-2 ring-orange-400' : ''}`}
                    onClick={() => setSelectedId(String(c.id))}
                  >
                    {c.title} · {formatClassWhen(c.start_time)}
                    {hasRecording ? ' · ▶' : ''}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </section>

      {selected ? (
        <>
          <section className="card p-4 space-y-3">
            <h2 className="font-semibold">{selected.title}</h2>
            <p className="text-xs text-stone-500">{formatClassWhen(selected.start_time)}</p>
            <div className="space-y-2">
              <label className="ui-field__label flex items-center gap-2">
                <Video className="w-4 h-4" />
                {selected.zoom_link
                  ? t('d28d.host.zoom_link', 'Enlace Zoom de la clase')
                  : t('d28d.host.zoom_missing', 'Publicar enlace Zoom para alumnos')}
              </label>
              {!selected.zoom_link ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {t(
                    'd28d.host.zoom_missing_hint',
                    'Esta clase aún no tiene enlace. Pégalo aquí para que los inscritos puedan entrar desde su calendario.',
                  )}
                </p>
              ) : null}
              <input
                className="ui-input w-full"
                placeholder="https://zoom.us/j/12345678901"
                value={zoomLinkDraft}
                onChange={(e) => setZoomLinkDraft(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={busy || !zoomLinkDraft.trim()}
                  onClick={saveZoomLink}
                >
                  {selected.zoom_link
                    ? t('d28d.host.zoom_update', 'Actualizar enlace')
                    : t('d28d.host.zoom_publish', 'Publicar enlace')}
                </button>
                {selected.zoom_link ? (
                  <a href={selected.zoom_link} target="_blank" rel="noreferrer" className="btn-secondary inline-flex text-sm">
                    {t('d28d.host.open_zoom', 'Abrir Zoom')}
                  </a>
                ) : null}
              </div>
            </div>
            <LiveClassRoutineHost
              classItem={selected}
              gymScoped={isGymHost}
              editable
              onRoutineUpdated={load}
            />
          </section>

          <section className="card p-4 space-y-3">
            <h2 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> {t('d28d.host.attendance', 'Asistencia')}</h2>
            {enrolled.length === 0 ? (
              <p className="text-sm text-stone-600">{t('d28d.host.no_enrolled', 'Sin inscritos aún.')}</p>
            ) : (
              <ul className="space-y-2">
                {enrolled.map((row) => {
                  const uid = Number(row?.user_id ?? row);
                  const present = (selected.attendance_user_ids || []).some((id) => Number(id) === uid);
                  const label = String(row?.nombre || row?.email || '').trim()
                    || t('d28d.host.user_fallback', 'Usuario #{id}').replace('{id}', String(uid));
                  return (
                    <li key={uid} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2">
                      <span>{label}</span>
                      <button
                        type="button"
                        className={`text-xs px-2 py-1 rounded-full ${present ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100'}`}
                        disabled={busy}
                        onClick={() => markAttendance(uid, !present)}
                      >
                        {present ? <CheckCircle className="w-3 h-3 inline" /> : null}
                        {present ? t('d28d.host.present', 'Presente') : t('d28d.host.mark', 'Marcar')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="card p-4 space-y-3">
            <h2 className="font-semibold flex items-center gap-2"><Video className="w-4 h-4" /> {t('d28d.host.recording', 'Publicar grabación')}</h2>
            {selectedRecording ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex flex-wrap items-center justify-between gap-2">
                <span>{t('d28d.host.already_published', 'Esta clase ya tiene grabación publicada.')}</span>
                <button type="button" className="btn-secondary text-xs" onClick={() => setPreviewRecording(selectedRecording)}>
                  {t('d28d.host.view_recording', 'Ver grabación')}
                </button>
              </div>
            ) : null}
            <input
              className="ui-input w-full"
              placeholder="https://… URL de la grabación"
              value={recordingUrl}
              onChange={(e) => setRecordingUrl(e.target.value)}
            />
            <button type="button" className="btn-primary text-sm" disabled={busy || !recordingUrl.trim()} onClick={publishRecording}>
              {isGymHost
                ? t('d28d.host.publish_gym', 'Publicar en galería de clases')
                : t('d28d.host.publish', 'Publicar en galería D28D')}
            </button>
          </section>
        </>
      ) : null}

      <ClassRecordingPreviewModal item={previewRecording} onClose={() => setPreviewRecording(null)} />
    </div>
  );
}
